/** Predefined annual salary buckets (SAR) for Careers filters. */

export type SalaryBucketId =
  | "under_72"
  | "72_120"
  | "120_180"
  | "180_240"
  | "240_360"
  | "360_480"
  | "480_plus";

export type SalaryBucket = {
  id: SalaryBucketId;
  /** Inclusive lower bound in absolute currency units (annual SAR). */
  min: number;
  /** Exclusive upper bound; Infinity for open-ended. */
  max: number;
};

export const SALARY_BUCKETS: SalaryBucket[] = [
  { id: "under_72", min: 0, max: 72_000 },
  { id: "72_120", min: 72_000, max: 120_000 },
  { id: "120_180", min: 120_000, max: 180_000 },
  { id: "180_240", min: 180_000, max: 240_000 },
  { id: "240_360", min: 240_000, max: 360_000 },
  { id: "360_480", min: 360_000, max: 480_000 },
  { id: "480_plus", min: 480_000, max: Number.POSITIVE_INFINITY },
];

export function isSalaryBucketId(value: string): value is SalaryBucketId {
  return SALARY_BUCKETS.some((b) => b.id === value);
}

export function getSalaryBucket(id: string): SalaryBucket | null {
  return SALARY_BUCKETS.find((b) => b.id === id) ?? null;
}

/**
 * Pull numeric yearly amounts from free-text salary fields
 * (e.g. "80k–110k SAR", "80000-110000", "80-100k").
 */
export function parseSalaryAmounts(text: string): number[] {
  const amounts: number[] = [];
  const re = /(\d+(?:[.,]\d+)?)\s*(k|m)?/gi;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    const raw = Number(match[1].replace(/,/g, ""));
    if (!Number.isFinite(raw)) continue;
    const suffix = (match[2] || "").toLowerCase();
    let value = raw;
    if (suffix === "k") value = raw * 1_000;
    else if (suffix === "m") value = raw * 1_000_000;
    else if (raw > 0 && raw < 1_000) value = raw * 1_000; // bare "80" → 80k
    if (value > 0) amounts.push(value);
  }
  return amounts;
}

/** True when the job's free-text salary overlaps the selected bucket. */
export function jobMatchesSalaryBucket(
  salaryText: string | null | undefined,
  bucketId: string,
): boolean {
  const bucket = getSalaryBucket(bucketId);
  if (!bucket) return true;
  if (!salaryText?.trim()) return false;
  const amounts = parseSalaryAmounts(salaryText);
  if (amounts.length === 0) return false;
  const jobMin = Math.min(...amounts);
  const jobMax = Math.max(...amounts);
  return jobMin < bucket.max && jobMax >= bucket.min;
}
